import { type QueryKey } from "@tanstack/react-query";

import { AggregateMetricsRequest, QueryMetricsRequest } from "../MetricRecord";

/**
 * Query key factory for metrics queries. Shared by all metrics query and
 * mutation hooks so the read and write sides cannot drift, and so invalidation
 * can target a whole stage of the hierarchy (all definitions, a single
 * definition, all queries, one session, etc.).
 *
 * The metrics API spans a few independent resources (definitions, raw query,
 * aggregate, per-session), so this factory exposes a sub-tree per resource
 * rather than a single root. Each `*All` / list entry is a prefix of its more
 * specific entries, so `invalidateQueries` against the broader key reaches the
 * narrower ones.
 *
 * `orgId` is hoisted to a fixed position immediately after each resource root,
 * so it lives in a deterministic place across every key (metrics data never
 * crosses org boundaries). On the resource-root accessors an undefined `orgId`
 * is omitted rather than encoded as a literal `undefined`: that keeps the
 * org-agnostic root a genuine prefix of the org-scoped keys, so a broad
 * invalidation still partial-matches the narrower ones. (A trailing/embedded
 * `undefined` is a real array element and would break partial-match
 * invalidation.)
 *
 * A leaf that embeds its own discriminator *after* `orgId` (e.g. `session`,
 * which appends `sessionId`) cannot use that omit trick: dropping an undefined
 * `orgId` would slide the discriminator into the org slot, so the key would no
 * longer prefix-match the org-scoped read it is meant to invalidate. Such
 * leaves require `orgId` — in the UI it is always defined past authentication,
 * and a write always knows the org it targets.
 *
 * @see https://tkdodo.eu/blog/effective-react-query-keys#use-query-key-factories
 */
export const metricsQueryKeys = {
  // --- Metric definitions ---
  definitions: (orgId?: string) =>
    orgId === undefined
      ? (["metricDefinitions"] as const)
      : (["metricDefinitions", orgId] as const),
  definitionsAll: (orgId: string | undefined) =>
    [...metricsQueryKeys.definitions(orgId), "all"] as const,

  // A single metric definition, keyed by name. Distinct top-level namespace so
  // invalidating the definitions list does not touch individual-definition
  // caches (and vice versa).
  definition: (name: string | undefined, orgId: string) =>
    ["metricDefinition", orgId, name] as const,

  // --- Raw metric query ---
  queries: (orgId?: string) =>
    orgId === undefined
      ? (["metricsQuery"] as const)
      : (["metricsQuery", orgId] as const),
  queriesInfinite: (
    request: QueryMetricsRequest | undefined,
    orgId: string | undefined,
  ) => [...metricsQueryKeys.queries(orgId), "infinite", request] as const,

  // --- Aggregate ---
  aggregates: (orgId?: string) =>
    orgId === undefined
      ? (["metricsAggregate"] as const)
      : (["metricsAggregate", orgId] as const),
  aggregate: (
    request: AggregateMetricsRequest | undefined,
    orgId: string | undefined,
  ) => [...metricsQueryKeys.aggregates(orgId), request] as const,

  // --- Per-session metrics ---
  sessions: (orgId?: string) =>
    orgId === undefined
      ? (["metricsBySession"] as const)
      : (["metricsBySession", orgId] as const),
  // A single session's metrics. Each option present is appended after the
  // session ID, so the key with both omitted prefixes every read's key.
  session: (
    sessionId: string | undefined,
    orgId: string,
    limit?: number,
    excludeNamePrefix?: string,
  ) =>
    [
      ...metricsQueryKeys.sessions(orgId),
      sessionId,
      ...(limit === undefined ? [] : [limit]),
      ...(excludeNamePrefix === undefined ? [] : [{ excludeNamePrefix }]),
    ] as const,
};

/**
 * `queryKeyHashFn` for the raw-query hooks (`queriesInfinite`). Their keys
 * embed a `QueryMetricsRequest` whose `start_time_ns` / `end_time_ns` are
 * `bigint`, and TanStack Query's default hashing is `JSON.stringify`, which
 * throws on `bigint`. Stringifying with a bigint→string replacer yields the
 * stable unique string the hash needs. The default also sorts plain-object
 * keys; we don't, which is fine because these keys are built deterministically
 * (same field order every render).
 */
export function hashMetricsQueryKey(queryKey: QueryKey): string {
  return JSON.stringify(queryKey, (_key, value: unknown) =>
    typeof value === "bigint" ? value.toString() : value,
  );
}
